import { all, put, takeEvery } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";

import { addressValidation, duplicateCheck } from "API/Omikron";
import { IAddressValidationResult } from "Model/Omikron/IAddressValidationResult";
import {
  addressValidationDialog,
  addressValidationError,
  addressValidationStart,
  addressValidationSuccess,
  addressValidationSuccessEmpty,
  duplicateCheckDialog,
  duplicateCheckError,
  duplicateCheckStart,
  duplicateCheckSuccess,
  importError,
  importStart,
  importSuccess,
  syncError,
  syncStart,
  syncSuccess,
  uiNotificationAdd
} from "Store/Actions";
import { hasElements } from "Utils/Array";
import { importStatus, importProjects } from "Workers/Projects";
import { syncProjects } from "API/Project";
import { callWithAuth } from "./CallWithAuth";

function* addressValidationStartSaga({ payload: idList }: PayloadAction<string[]>) {
  try {
    const response: IAddressValidationResult[][] = yield callWithAuth(addressValidation, idList);
    const hasAddresses = hasElements(response) && response.some((addressList) => hasElements(addressList));

    if (!hasAddresses) {
      yield put(addressValidationSuccessEmpty());
    }

    yield all([put(addressValidationSuccess(response)), put(addressValidationDialog(true))]);
  } catch (e) {
    console.error(e);
    yield put(addressValidationError(isDQAvailabilityError(e) ? { text: `omikron.400` } : undefined));
  }
}

function* duplicateCheckStartSaga({ payload: idList }: PayloadAction<string[]>) {
  try {
    const checkedAddresses = yield callWithAuth(duplicateCheck, idList);
    yield all([put(duplicateCheckSuccess(checkedAddresses)), put(duplicateCheckDialog(true))]);
  } catch (e) {
    console.error(e);
    yield put(duplicateCheckError(isDQAvailabilityError(e) ? { text: `omikron.400` } : undefined));
  }
}

function isDQAvailabilityError(error: Error): boolean {
  const errorSubstrings = [
    `currently not available`,
    `DQServerAvailable`,
    `The service will be available in a few minutes`
  ];

  return errorSubstrings.some((item) => error.toString().includes(item));
}

function* importStartSaga() {
  try {
    if (yield callWithAuth(importStatus)) {
      yield all([
        put(importError()),
        put(uiNotificationAdd({ text: `Import failed. Currently another import is in progress.`, type: `error` }))
      ]);
      return;
    }

    yield callWithAuth(importProjects);
    yield put(importSuccess());
  } catch (e) {
    console.error(e);
    yield put(importError());
  }
}

function* syncStartSaga({ payload }: PayloadAction<{ id: string; userName: string; lastXMonths: number }>) {
  const { id, userName, lastXMonths } = payload;
  try {
    if (yield callWithAuth(importStatus)) {
      yield all([
        put(importError()),
        put(uiNotificationAdd({ text: `Sync failed. Currently another import is in progress.`, type: `error` }))
      ]);
      return;
    }

    yield callWithAuth(syncProjects, id, userName, lastXMonths);
    yield put(syncSuccess());
  } catch (e) {
    console.error(e);
    yield put(syncError());
  }
}

export function* BackendSaga(): unknown {
  yield all([
    takeEvery(addressValidationStart, addressValidationStartSaga),
    takeEvery(duplicateCheckStart, duplicateCheckStartSaga),
    takeEvery(importStart, importStartSaga),
    takeEvery(syncStart, syncStartSaga)
  ]);
}
